import { ADD_PRODUCT_BASIC, type addBaseProductDataAction } from "./actions";
import { isKITProduct, type ProductData } from "../../models/ProductData";
import {
  type fetchSRPCampaignRecommendationsSuccess,
  SEARCH_SUCCESS,
  type searchSuccessAction,
  SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
} from "../../Search/redux/actions";
import { type TransformedCartData } from "../../Cart/transformers/CartViewDataTransformer";
import { type loadWishlistNamesSuccessAction } from "../../Wishlist/redux/actions";
import { LOAD_WISHLISTS_SUCCESS } from "~/Wishlist/redux/actionTypes";
import { combineReducers } from "redux";
import { CHECKOUT_LOAD_DATA, type checkoutLoadDataAction } from "../../Cart/redux/actions/checkoutPageActions";
import { availabilityReducerData, priceReducerData } from "./pnaReducers";
import {
  HSA_SEARCH_SUCCESS,
  LOAD_INITIAL_DATA,
  type loadInitialDataAction,
  type searchSuccessAction as hsaSearchSuccessAction,
} from "../../HousingSelectionAssistant/redux/actions";
import { CART_LOAD_SUCCESS, type cartLoadFinishAction } from "~/Cart/pages/CartPage/redux/actions";
import { produce } from "immer";

import { isNonNull } from "~/utils/reducers";
import { FINISH_FETCH_RELATED_PRODUCTS, type RelatedProductsActions } from "~/redux/products/relatedProducts/actions";
import { relatedProductsReducer } from "~/redux/products/relatedProducts/reducer";
import { topsellerProductsReducer } from "./topsellerProducts/reducer";
import { FINISH_FETCH_TOPSELLER_PRODUCTS, type TopsellerProductsActions } from "./topsellerProducts/actions";
import type { SrpProductData } from "~/utils/models";
import { fccRecommendedProductsReducer } from "./fccRecommendationProducts/reducer";
import {
  type FCCRecommendedProductsActions,
  FINISH_FETCH_FCC_RECOMMENDED_PRODUCTS,
} from "./fccRecommendationProducts/actions";

type BaseProductStore = { [code: string]: ProductData };

export type BaseProductReducerActions =
  | ReturnType<typeof searchSuccessAction>
  | ReturnType<typeof cartLoadFinishAction>
  | ReturnType<typeof loadWishlistNamesSuccessAction>
  | ReturnType<typeof checkoutLoadDataAction>
  | ReturnType<typeof addBaseProductDataAction>
  | ReturnType<typeof loadInitialDataAction>
  | ReturnType<typeof hsaSearchSuccessAction>
  | ReturnType<typeof fetchSRPCampaignRecommendationsSuccess>
  | RelatedProductsActions
  | TopsellerProductsActions
  | FCCRecommendedProductsActions;

const baseProductsReducer = (state: BaseProductStore = {}, action: BaseProductReducerActions): BaseProductStore => {
  switch (action.type) {
    case SEARCH_SUCCESS: {
      const { data } = action.payload;
      return updateBasicProducts(state, data.products);
    }

    case CART_LOAD_SUCCESS: {
      const data = action.payload as TransformedCartData;
      return updateBasicProducts(state, Object.values(data.productByCode));
    }

    case LOAD_WISHLISTS_SUCCESS: {
      const data = action.payload;
      return updateBasicProducts(state, data.products);
    }

    case CHECKOUT_LOAD_DATA: {
      const { data } = action.payload;
      return updateBasicProducts(state, Object.values(data.productByCode));
    }

    case LOAD_INITIAL_DATA:
    case HSA_SEARCH_SUCCESS: {
      return updateBasicProducts(state, action.payload.searchResults.products);
    }

    case ADD_PRODUCT_BASIC: {
      const { products } = action.payload;
      return updateBasicProducts(state, products);
    }

    case FINISH_FETCH_RELATED_PRODUCTS: {
      const { relatedProducts } = action.payload;
      return updateBasicProducts(state, relatedProducts as ProductData[]);
    }

    case FINISH_FETCH_TOPSELLER_PRODUCTS: {
      const { topsellerProducts } = action.payload;
      return updateBasicProducts(state, topsellerProducts as ProductData[]);
    }

    case SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS: {
      const products = action.payload as SrpProductData[];
      return updateBasicProducts(state, products);
    }

    case FINISH_FETCH_FCC_RECOMMENDED_PRODUCTS: {
      const { fccRecommendedProducts } = action.payload;
      return updateBasicProducts(state, fccRecommendedProducts as ProductData[]);
    }

    default:
      return state;
  }
};

export const productsReducer = combineReducers({
  basic: baseProductsReducer,
  price: priceReducerData.reducer,
  availability: availabilityReducerData.reducer,
  relatedProducts: relatedProductsReducer,
  topsellerProducts: topsellerProductsReducer,
  fccRecommendedProducts: fccRecommendedProductsReducer,
});

function updateBasicProducts(state: BaseProductStore, products: ProductData[]) {
  // Do not add products without a code
  products = products.filter((product) => product.code);
  const newProducts = [
    ...products.map((product) => product.successorProduct),
    ...products.flatMap((product) => product.alternativeProducts),
    ...products.flatMap((product) => {
      return isKITProduct(product) ? product.includedParts.map((i) => i.product) : [];
    }),
    ...products.flatMap((product) => {
      return isKITProduct(product) ? product.optionalParts.map((i) => i.product) : [];
    }),
    ...products,
  ].filter(isNonNull);

  return produce(state, (draft) => {
    newProducts.forEach((product) => {
      const oldProduct = draft[product.code];
      if (oldProduct?.isFullProduct) {
        return;
      }
      if (oldProduct) {
        draft[product.code] = merge(oldProduct, product);
      } else {
        draft[product.code] = product;
      }
    });
  });
}

function merge<T extends object>(base: T, other: T): T {
  const result = { ...base };
  // Add all properties from other as long as they are not equal undefined
  Object.keys(other).forEach((key) => {
    const thisValue = base[key];
    const otherValue = other[key];

    if (!isOverridableValue(otherValue)) {
      return;
    }

    if (isArrayValue(thisValue, otherValue)) {
      result[key] = overrideArray(thisValue, otherValue);
    } else if (thisValue && typeof thisValue === "object" && otherValue && typeof otherValue === "object") {
      if (Object.keys(thisValue).length > 0 && Object.keys(otherValue).length > 0) {
        result[key] = merge(thisValue, otherValue);
      } else if (Object.keys(thisValue).length === 0) {
        result[key] = otherValue;
      }
    } else {
      result[key] = otherValue;
    }
  });
  return result;
}

/**
 * The property is considered an array value if either this or other has an array in that property
 * @param thisValue
 * @param otherValue
 */
function isArrayValue(thisValue: unknown, otherValue: unknown) {
  return Array.isArray(thisValue) || Array.isArray(otherValue);
}

/**
 * If other is an array with items, return other.
 * If this is an array with items, return this.
 * Otherwise just return the one that is an array
 * @param thisValue
 * @param otherValue
 */
function overrideArray(thisValue: unknown, otherValue: unknown) {
  if (Array.isArray(otherValue) && otherValue.length > 0) {
    return otherValue;
  }
  if (Array.isArray(thisValue) && thisValue.length > 0) {
    return thisValue;
  }
  return Array.isArray(otherValue) ? otherValue : thisValue;
}

function isOverridableValue(value: unknown) {
  if (value === undefined) {
    return false;
  }

  return true;
}
